// ===========================================
// Pricing Matrix Tab
// ===========================================
// Editable grid for Program Level x Duration x Category pricing
// Option B: Expanded columns with Group | Individual sub-headers

import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Alert,
  AlertTitle,
  Link,
  CircularProgress,
  Typography,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { usePricingMatrix, useBulkUpdatePricing, useRequiredPricing } from '../../../hooks/usePricing';
import type { BulkPriceUpdate, PricingCategory, RequiredPriceCell, RequiredPriceClass } from '../../../api/pricing.api';

// ===========================================
// TYPES
// ===========================================

interface PriceValue {
  programLevelId: string;
  durationId: string;
  category: PricingCategory;
  price: string;
  originalPrice: number | null;
  id: string | null;
}

// ===========================================
// PRICE INPUT COMPONENT
// ===========================================

interface PriceInputProps {
  programLevelId: string;
  durationId: string;
  category: PricingCategory;
  priceValues: Map<string, PriceValue>;
  /** Invoicing will ask for this cell (a current or upcoming class bills it). */
  required?: RequiredPriceCell;
  onPriceChange: (programLevelId: string, durationId: string, category: PricingCategory, value: string) => void;
}

/**
 * A typed/stored price that invoicing cannot bill: $0 or less. Invoicing treats it as NO
 * price (billablePrice) and the API refuses to save it — "no price" is a cleared cell.
 */
function isUnbillablePrice(value: string): boolean {
  const n = parseFloat(value);
  return value.trim() !== '' && !isNaN(n) && n <= 0;
}

/** A typed value invoicing can bill (what `billablePrice` accepts). */
function isBillablePrice(value: string): boolean {
  const n = parseFloat(value);
  return !isNaN(n) && n > 0;
}

/** "Wed 4:15PM Voice: Foundation 1 (Term 4 2026), … and 2 more" — first three, then a count. */
function describeClasses(classes: RequiredPriceClass[]): string {
  const shown = classes.slice(0, 3).map((c) => `${c.lessonName.trim()} (${c.termName})`);
  const more = classes.length - shown.length;
  return more > 0 ? `${shown.join(', ')} and ${more} more` : shown.join(', ');
}

function PriceInput({ programLevelId, durationId, category, priceValues, required, onPriceChange }: PriceInputProps) {
  const key = `${programLevelId}-${durationId}-${category}`;
  const priceValue = priceValues.get(key);
  const unbillable = isUnbillablePrice(priceValue?.price ?? '');
  // Live: typing a price (even unsaved) clears the outline, so the admin sees the fix land. A cell
  // still showing its LOADED value keeps the server's verdict: a positive price the run still cannot
  // use (another pricing row wins over it) stays outlined (QA L2).
  const price = priceValue?.price ?? '';
  const editedToBillable =
    isBillablePrice(price) &&
    (priceValue?.originalPrice === null ||
      priceValue?.originalPrice === undefined ||
      Math.abs(parseFloat(price) - priceValue.originalPrice) > 0.001);
  const missingRequired = !!required && !editedToBillable;

  const field = (
    <TextField
      error={unbillable || missingRequired}
      size="small"
      type="number"
      value={priceValue?.price ?? ''}
      onChange={(e) => onPriceChange(programLevelId, durationId, category, e.target.value)}
      placeholder="—"
      inputProps={{
        min: 0,
        step: 0.01,
        style: { textAlign: 'center' },
      }}
      InputProps={{
        startAdornment: priceValue?.price ? (
          <InputAdornment position="start">$</InputAdornment>
        ) : null,
      }}
      sx={{
        width: 100,
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          display: 'none',
        },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      }}
    />
  );

  // ALWAYS wrapped (empty title = no tooltip): wrapping only when unbillable changed the
  // tree mid-typing, remounting the input on the "0" of "0.50" and dropping focus.
  return (
    <Tooltip
      describeChild
      title={
        missingRequired
          ? isBillablePrice(price)
            ? `Invoicing still can't use this price for ${describeClasses(required!.classes)} — a separately stored price overrides this cell, and it isn't usable. Ask for help to remove it.`
            : `Invoicing needs this price for ${describeClasses(required!.classes)}.`
          : unbillable
          ? "$0 isn't billable — invoicing will ask for this price. Enter a price, or clear the cell if this combination doesn't apply."
          : ''
      }
    >
      {field}
    </Tooltip>
  );
}

// ===========================================
// PHONE LEVEL MARKER
// ===========================================

/** Phones only: the full-name column scrolls away there, so a slim locked column keeps a dot. */
const phoneOnly = { display: { xs: 'table-cell', sm: 'none' } } as const;
const LEVEL_MARKER_WIDTH = 28;

/**
 * The level's colour as a dot; a tap (or focus) shows the full name. Colours are per INSTRUMENT in
 * practice (every Piano level is blue), so the dot says which section you are in and the name says
 * which level. A level with no colour gets a neutral dot, so every row has one (Andrew 16/10/2026).
 */
function LevelMarker({
  name,
  color,
  isActive,
  needsPrice,
}: {
  name: string;
  color: string | null;
  isActive: boolean;
  /** A price invoicing needs is missing somewhere in this row — ringed red, so a phone user scanning
   * the left edge sees which rows to scroll along (the red cell itself may be off-screen). */
  needsPrice: boolean;
}) {
  const label = `${isActive ? name : `${name} (inactive)`}${needsPrice ? ' — missing a price invoicing needs' : ''}`;
  return (
    <Tooltip title={label} enterTouchDelay={0} leaveTouchDelay={3000} placement="right">
      <Box
        component="span"
        role="img"
        aria-label={label}
        tabIndex={0}
        sx={{
          display: 'block',
          width: 14,
          height: 14,
          mx: 'auto',
          borderRadius: '50%',
          bgcolor: color ?? 'grey.400',
          // An outline, not a shadow (brand: flat). Offset so the ring reads apart from the dot's colour.
          ...(needsPrice && { outline: '2px solid', outlineColor: 'error.main', outlineOffset: '2px' }),
        }}
      />
    </Tooltip>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

// The header is TWO sticky rows: the second sits under the first, so the first row's height is
// fixed and is the second row's `top`. Cells need an OPAQUE background or rows show through.
const HEADER_ROW_HEIGHT = 36;
// Cream, as the header always looked (it showed the Paper through); the stripes start on white.
// Phones (xs) lock the HEADER but not the level column: the columns are the hard part to remember,
// and a sticky 180 px column eats half a 360 px screen (QA L5, Andrew 16/10/2026).
const stickyHeaderCell = { bgcolor: 'background.paper' } as const;
/** Flat colour blocking (brand: no shading) — white and cream, alternating. */
const stripe = (index: number) => (index % 2 === 0 ? 'background.default' : 'background.paper');

export default function PricingMatrixTab() {
  const { data: matrix, isLoading, error } = usePricingMatrix();
  const bulkUpdateMutation = useBulkUpdatePricing();
  // Optional: a failed or slow check just means no outlines — the matrix still works.
  const {
    data: requiredPricing,
    isFetching: checkingRequired,
    isError: requiredCheckFailed,
  } = useRequiredPricing();

  // Track all price values in a map: `${programLevelId}-${durationId}-${category}` -> price
  const [priceValues, setPriceValues] = useState<Map<string, PriceValue>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Phones: the locked marker column shows its dot only once the level NAMES have scrolled under it
  // — before that the name (and its own dot) is on screen, and two dots read as a glitch (Andrew
  // 16/10/2026). Measured from the name header cell, so it follows the real column width.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const nameHeaderRef = useRef<HTMLTableCellElement>(null);
  const [namesScrolledPast, setNamesScrolledPast] = useState(false);
  const handleTableScroll = useCallback(() => {
    const container = tableScrollRef.current;
    const nameHeader = nameHeaderRef.current;
    if (!container || !nameHeader || nameHeader.offsetWidth === 0) return; // hidden/unmeasured: leave as is
    // Past = the name column's right edge has slid behind the marker column.
    const passed = container.scrollLeft + LEVEL_MARKER_WIDTH >= nameHeader.offsetLeft + nameHeader.offsetWidth;
    setNamesScrolledPast((prev) => (prev === passed ? prev : passed));
  }, []);

  const categories: PricingCategory[] = ['GROUP', 'INDIVIDUAL'];

  // Initialize price values when data loads
  useEffect(() => {
    if (matrix) {
      const newPriceValues = new Map<string, PriceValue>();

      // Initialize with empty values for all combinations (programLevel x duration x category)
      matrix.programLevels.forEach((pl) => {
        matrix.durations.forEach((d) => {
          categories.forEach((category) => {
            const key = `${pl.id}-${d.id}-${category}`;
            // A cleared (inactive) price reads back BLANK. The API already returns active
            // prices only; the isActive guard keeps a stale response from resurrecting one.
            const existingPrice = matrix.prices.find(
              (p) =>
                p.programLevelId === pl.id && p.durationId === d.id && p.category === category && p.isActive
            );

            newPriceValues.set(key, {
              programLevelId: pl.id,
              durationId: d.id,
              category,
              price: existingPrice ? existingPrice.price.toFixed(2) : '',
              originalPrice: existingPrice?.price ?? null,
              id: existingPrice?.id ?? null,
            });
          });
        });
      });

      setPriceValues(newPriceValues);
      setHasChanges(false);
    }
  }, [matrix]);

  // Handle price change
  const handlePriceChange = useCallback(
    (programLevelId: string, durationId: string, category: PricingCategory, value: string) => {
      const key = `${programLevelId}-${durationId}-${category}`;
      setPriceValues((prev) => {
        const newMap = new Map(prev);
        const existing = prev.get(key);
        if (existing) {
          newMap.set(key, { ...existing, price: value });
        }
        return newMap;
      });
      setHasChanges(true);
    },
    []
  );

  // Get changed prices for saving
  const changedPrices = useMemo(() => {
    const changes: BulkPriceUpdate[] = [];

    priceValues.forEach((value) => {
      // A cell that HAD a price and is now blank is a CLEAR — sent as `price: null`, which
      // switches the price off so invoicing asks for it. Until 2026-09-24 a blank cell was
      // silently dropped, the Save button stayed disabled, and admins typed 0 to mean "no
      // price" — which invoicing then billed as $0 (MNM: 46 such cells, 8 live classes).
      if (value.price.trim() === '') {
        if (value.originalPrice !== null) {
          changes.push({
            programLevelId: value.programLevelId,
            durationId: value.durationId,
            category: value.category,
            price: null,
          });
        }
        return;
      }
      const newPrice = parseFloat(value.price);
      // $0 is never saved (the API refuses it): it is flagged in the grid instead.
      if (!isNaN(newPrice) && newPrice > 0) {
        // Only include if changed from original or is a new price
        if (value.originalPrice === null || Math.abs(newPrice - value.originalPrice) > 0.001) {
          changes.push({
            programLevelId: value.programLevelId,
            durationId: value.durationId,
            category: value.category,
            price: newPrice,
          });
        }
      }
    });

    return changes;
  }, [priceValues]);

  // Cells showing $0 — MNM's matrix had 46, typed to mean "no price" before a blank
  // could be saved. Counted so they can be found and cleared rather than discovered
  // one invoice at a time.
  const unbillableCount = useMemo(
    () => Array.from(priceValues.values()).filter((v) => isUnbillablePrice(v.price)).length,
    [priceValues]
  );

  // The cells invoicing will ask for, keyed like `priceValues`. The server names a cell by its
  // minutes; a length with no duration column cannot be outlined, so it is listed instead.
  const { requiredByKey, requiredWithoutColumn } = useMemo(() => {
    const byKey = new Map<string, RequiredPriceCell>();
    const withoutColumn: RequiredPriceCell[] = [];
    for (const cell of requiredPricing?.cells ?? []) {
      const duration = matrix?.durations.find((d) => d.minutes === cell.durationMinutes);
      if (duration) byKey.set(`${cell.programLevelId}-${duration.id}-${cell.category}`, cell);
      else withoutColumn.push(cell);
    }
    // One notice per missing length, so "90 min is not set up" is said once however many classes use it.
    const byMinutes = new Map<number, RequiredPriceCell[]>();
    for (const cell of withoutColumn) byMinutes.set(cell.durationMinutes, [...(byMinutes.get(cell.durationMinutes) ?? []), cell]);
    const missingLengths = Array.from(byMinutes.entries())
      .sort(([a], [b]) => a - b)
      .map(([minutes, cells]) => ({ minutes, cells }));
    return { requiredByKey: byKey, requiredWithoutColumn: missingLengths };
  }, [requiredPricing, matrix]);

  // Must agree with PriceInput's `missingRequired`: a cell counts until it is EDITED to a usable price.
  const requiredMissingKeys = useMemo(
    () =>
      Array.from(requiredByKey.keys()).filter((key) => {
        const v = priceValues.get(key);
        if (!v || !isBillablePrice(v.price)) return true;
        return v.originalPrice !== null && Math.abs(parseFloat(v.price) - v.originalPrice) <= 0.001;
      }),
    [requiredByKey, priceValues]
  );
  const requiredMissingCount = requiredMissingKeys.length;
  // Rows holding at least one of them — their level dot is ringed red.
  const levelsMissingPrice = useMemo(
    () => new Set(requiredMissingKeys.map((key) => requiredByKey.get(key)!.programLevelId)),
    [requiredMissingKeys, requiredByKey]
  );
  // Only the terms that actually need one of the outlined cells.
  const requiredTermNames = useMemo(
    () => [
      ...new Set(requiredMissingKeys.flatMap((key) => requiredByKey.get(key)!.classes.map((c) => c.termName))),
    ],
    [requiredMissingKeys, requiredByKey]
  );

  // Handle save
  const handleSave = async () => {
    if (changedPrices.length === 0) return;

    try {
      await bulkUpdateMutation.mutateAsync(changedPrices);
      setHasChanges(false);
    } catch {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error">
        Failed to load pricing matrix. Please try again.
      </Alert>
    );
  }

  // No data state
  if (!matrix || matrix.programLevels.length === 0 || matrix.durations.length === 0) {
    return (
      <Alert severity="info">
        Please configure Program Levels and Lesson Durations before setting up pricing.
        <br />
        Go to <strong>Class Configuration</strong> to add them.
      </Alert>
    );
  }

  // Sort program levels by sortOrder, durations by minutes
  const sortedProgramLevels = [...matrix.programLevels].sort((a, b) => a.sortOrder - b.sortOrder);
  const sortedDurations = [...matrix.durations].sort((a, b) => a.minutes - b.minutes);

  return (
    <Box>
      {/* Header with save button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Set base prices for each program level, duration, and lesson type.
          Leave empty (or clear) any combination that doesn't apply.
        </Typography>
        <Button
          variant="contained"
          startIcon={bulkUpdateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges || changedPrices.length === 0 || bulkUpdateMutation.isPending}
        >
          {bulkUpdateMutation.isPending ? 'Saving...' : 'Save All'}
        </Button>
      </Box>

      {unbillableCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unbillableCount} price{unbillableCount > 1 ? 's are' : ' is'} $0 (outlined in red). Invoicing
          can&apos;t bill $0 and will stop to ask for these — enter a price, or clear the cell if the
          combination doesn&apos;t apply.
        </Alert>
      )}

      {/* The check can take a few seconds (QA M2): say so, so "no red cells" is never mistaken for
          "nothing missing" while it runs, or after it fails. */}
      {checkingRequired ? (
        <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Checking which prices invoicing needs…</Typography>
        </Box>
      ) : (
        requiredCheckFailed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn&apos;t check which prices invoicing needs, so missing prices aren&apos;t outlined. Reload the
            page to try again.
          </Alert>
        )
      )}

      {requiredMissingCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {requiredMissingCount} price{requiredMissingCount > 1 ? 's' : ''} that invoicing needs{' '}
          {requiredMissingCount > 1 ? 'are' : 'is'} missing (outlined in red). Classes in{' '}
          {requiredTermNames.join(', ')} bill {requiredMissingCount > 1 ? 'these cells' : 'this cell'}, so
          invoicing can hold those families back until {requiredMissingCount > 1 ? 'they are' : 'it is'} set.
          Hover or tap a cell to see which classes need it — a class is priced only from its own program
          level&apos;s row.
        </Alert>
      )}

      {/* The matrix lists every duration the school has, active or not, so a length with no column
          is one that was never set up — the fix is in Class Configuration, not on this page. */}
      {requiredWithoutColumn.map(({ minutes, cells }) => (
        <Alert key={minutes} severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{minutes} min is missing from Class Configuration</AlertTitle>
          Invoicing needs {minutes} min prices for {cells.length === 1 ? 'this class' : 'these classes'}, but{' '}
          {minutes} min is not one of your lesson durations, so there is no column to price it in. Add it under{' '}
          <Link component={RouterLink} to="../durations">
            Class Configuration → Durations
          </Link>{' '}
          and a {minutes} min column appears here with the prices to fill outlined in red.
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {cells.map((c) => (
              <li key={`${c.programLevelId}-${c.category}`}>
                {c.programLevelName} · {c.category === 'GROUP' ? 'Group' : 'Individual'} — {describeClasses(c.classes)}
              </li>
            ))}
          </Box>
        </Alert>
      ))}

      {(requiredPricing?.noProgramLevel.length ?? 0) > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {requiredPricing!.noProgramLevel.length === 1 ? 'This class has' : 'These classes have'} no program
          level, so no price here can bill {requiredPricing!.noProgramLevel.length === 1 ? 'it' : 'them'} — set
          the level on the class: {describeClasses(requiredPricing!.noProgramLevel)}.
        </Alert>
      )}

      {hasChanges && changedPrices.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {changedPrices.length} unsaved change{changedPrices.length > 1 ? 's' : ''}.
        </Alert>
      )}

      {/* Pricing Matrix Table with Expanded Columns */}
      {/* Its own scroll area: a header cannot stick to the PAGE from inside a box that scrolls
          sideways, so the table scrolls within the screen and the header + level column stay put. */}
      {/* dvh on phones: 100vh includes the browser's address bar, which would hide the last rows. */}
      <TableContainer
        ref={tableScrollRef}
        onScroll={handleTableScroll}
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: { xs: 'calc(100dvh - 120px)', sm: 'calc(100vh - 220px)' } }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            {/* First Row: Duration headers spanning 2 columns each */}
            <TableRow>
              {/* Phones: the slim locked marker column's header (the corner). */}
              <TableCell
                rowSpan={2}
                aria-label="Program level colour"
                sx={{
                  ...stickyHeaderCell,
                  ...phoneOnly,
                  left: 0,
                  zIndex: 3,
                  width: LEVEL_MARKER_WIDTH,
                  minWidth: LEVEL_MARKER_WIDTH,
                  px: 0,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                }}
              />
              <TableCell
                ref={nameHeaderRef}
                rowSpan={2}
                sx={{
                  ...stickyHeaderCell,
                  left: { xs: 'auto', sm: 0 }, // the column only locks from sm up
                  zIndex: 3,
                  fontWeight: 'bold',
                  minWidth: 180,
                  verticalAlign: 'bottom',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                }}
              >
                Program Level
              </TableCell>
              {sortedDurations.map((duration) => (
                <TableCell
                  key={duration.id}
                  colSpan={2}
                  align="center"
                  sx={{
                    ...stickyHeaderCell,
                    height: HEADER_ROW_HEIGHT,
                    boxSizing: 'border-box',
                    py: 0,
                    fontWeight: 'bold',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '2px solid',
                    borderLeftColor: 'divider',
                  }}
                >
                  {duration.minutes} min
                  {!duration.isActive && (
                    <Chip
                      label="Inactive"
                      size="small"
                      sx={{ ml: 0.5, fontSize: '0.65rem', height: 16 }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>

            {/* Second Row: Group | Individual sub-headers */}
            <TableRow>
              {sortedDurations.map((duration) => (
                <Fragment key={duration.id}>
                  <TableCell
                    align="center"
                    sx={{
                      ...stickyHeaderCell,
                      top: HEADER_ROW_HEIGHT,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      minWidth: 100,
                      borderRight: '1px dashed',
                      borderColor: 'divider',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      borderLeft: '2px solid',
                      borderLeftColor: 'divider',
                      py: 0.5,
                    }}
                  >
                    Group
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      ...stickyHeaderCell,
                      top: HEADER_ROW_HEIGHT,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      minWidth: 100,
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      py: 0.5,
                    }}
                  >
                    Individual
                  </TableCell>
                </Fragment>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedProgramLevels.map((programLevel, index) => (
              <TableRow
                key={programLevel.id}
                sx={{
                  bgcolor: stripe(index),
                  opacity: programLevel.isActive ? 1 : 0.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* Phones: the slim locked marker; the name column below scrolls away there. */}
                <TableCell
                  sx={{
                    ...phoneOnly,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: stripe(index),
                    width: LEVEL_MARKER_WIDTH,
                    minWidth: LEVEL_MARKER_WIDTH,
                    px: 0,
                  }}
                >
                  {namesScrolledPast && (
                    <LevelMarker
                      name={programLevel.name}
                      color={programLevel.color}
                      isActive={programLevel.isActive}
                      needsPrice={levelsMissingPrice.has(programLevel.id)}
                    />
                  )}
                </TableCell>
                {/* Sticky on the left from sm up: the level stays named while scrolling to the long durations. */}
                <TableCell sx={{ position: { xs: 'static', sm: 'sticky' }, left: 0, zIndex: 1, bgcolor: stripe(index) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(programLevel.color || levelsMissingPrice.has(programLevel.id)) && (
                      <Box
                        aria-hidden
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: programLevel.color ?? 'grey.400',
                          flexShrink: 0,
                          ...(levelsMissingPrice.has(programLevel.id) && {
                            outline: '2px solid',
                            outlineColor: 'error.main',
                            outlineOffset: '2px',
                          }),
                        }}
                      />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {programLevel.name}
                    </Typography>
                    {!programLevel.isActive && (
                      <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                    )}
                  </Box>
                </TableCell>

                {sortedDurations.map((duration) => (
                  <Fragment key={duration.id}>
                    {/* Group Price Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        borderRight: '1px dashed',
                        borderColor: 'divider',
                        borderLeft: '2px solid',
                        borderLeftColor: 'divider',
                      }}
                    >
                      <PriceInput
                        programLevelId={programLevel.id}
                        durationId={duration.id}
                        category="GROUP"
                        priceValues={priceValues}
                        required={requiredByKey.get(`${programLevel.id}-${duration.id}-GROUP`)}
                        onPriceChange={handlePriceChange}
                      />
                    </TableCell>

                    {/* Individual Price Cell */}
                    <TableCell align="center">
                      <PriceInput
                        programLevelId={programLevel.id}
                        durationId={duration.id}
                        category="INDIVIDUAL"
                        priceValues={priceValues}
                        required={requiredByKey.get(`${programLevel.id}-${duration.id}-INDIVIDUAL`)}
                        onPriceChange={handlePriceChange}
                      />
                    </TableCell>
                  </Fragment>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
